// STERILTRON product catalog — exact model list.
// Hierarchy: Product Line -> Door Type -> Model

const PIMG = {
  steamSmall: "/assets/green/steam-small.jpg",
  steamMed: "/assets/green/steam-med.jpg",
  steamLarge: "/assets/green/steam-large.jpg",
  plasmaSingle: "/assets/green/plasma-single.jpg",
  washerDouble: "/assets/green/washer-double.jpg",
};

export const DOOR_TYPES = ["Single Door", "Double Door"];

const steamImg = (s) => (s <= 200 ? PIMG.steamSmall : s <= 330 ? PIMG.steamMed : PIMG.steamLarge);

const LINES = [
  {
    line: "Steam Sterilizers",
    doors: [
      { door: "Single Door", prefix: "SN", models: ["160", "250", "330", "420", "555", "650", "750", "850"] },
      { door: "Double Door", prefix: "SD", models: ["160", "250", "330", "420", "555", "650", "750", "850"] },
    ],
    img: (s) => steamImg(s),
    tagline: (m, door, s) => `${door} steam sterilizer with a ${s} L AISI 316L chamber for validated, high-throughput reprocessing.`,
    intro: (m, door, s) => `The ${m} is a ${door.toLowerCase()} saturated-steam sterilizer engineered for the validated reprocessing of instruments, textiles and porous loads in demanding healthcare and laboratory environments.`,
    overview: (m, door, s) => `The ${m} delivers repeatable, fully documented sterilization from a ${s}-litre AISI 316L chamber. Fractionated pre-vacuum cycles ensure complete steam penetration of wrapped and porous loads, while a programmable control system records every phase for complete assurance. The ${door.toLowerCase()} configuration suits ${door === "Double Door" ? "barrier workflows that separate the non-sterile and sterile sides of a department" : "standard central and departmental installations"}.`,
    benefits: (door) => [
      "Fractionated pre-vacuum cycle for complete steam penetration",
      "AISI 316L chamber engineered for decades of continuous duty",
      door === "Double Door" ? "Pass-through double-door barrier separating clean and sterile zones" : "Compact single-door design for standard installations",
      "Full digital cycle logging for regulatory compliance",
    ],
    applications: ["Hospital central sterile services (CSSD)", "Surgical instrument reprocessing", "Textile and porous-load sterilization", "Laboratory glassware and media"],
    technology: "Fractionated pre-vacuum pulses evacuate air from the chamber and load before saturated steam is admitted, guaranteeing penetration into lumens and packs. Temperature, pressure and time are recorded throughout each cycle for EN 285 compliant validation.",
    techFeatures: (door) => [`${door} configuration`, "Fractionated pre-vacuum cycle", "AISI 316L welded pressure vessel", "Touchscreen PLC control with cycle archiving", "Independent process-monitoring channel", "USB / network cycle-data export"],
    specs: (m, door, s) => [["Model", m], ["Door type", door], ["Chamber volume", `${s} L`], ["Standard", "EN 285"], ["Operating temperature", "121 – 134 °C"], ["Vessel material", "AISI 316L"]],
    options: ["Integrated clean-steam generator", "Loading trolley and transfer carriage", "Validation and traceability software suite", "Heat-recovery package"],
  },
  {
    line: "Plasma Sterilizers",
    doors: [
      { door: "Single Door", prefix: "PN", models: ["80", "110", "160"] },
      { door: "Double Door", prefix: "PD", models: ["110", "160"] },
    ],
    img: () => PIMG.plasmaSingle,
    tagline: (m, door, s) => `${door} low-temperature hydrogen peroxide plasma sterilizer with a ${s} L chamber for heat-sensitive instruments.`,
    intro: (m, door, s) => `The ${m} is a ${door.toLowerCase()} low-temperature sterilizer that uses vaporised hydrogen peroxide and plasma to process heat- and moisture-sensitive instruments quickly, safely and without toxic residues.`,
    overview: (m, door, s) => `The ${m} sterilizes delicate and heat-sensitive devices in a ${s}-litre chamber at temperatures below 55 °C. A measured dose of hydrogen peroxide is vaporised, diffused and energised into a low-temperature plasma, then broken down into water and oxygen — leaving instruments dry, residue-free and ready for immediate use.`,
    benefits: (door) => [
      "Sterilizes heat- and moisture-sensitive instruments below 55 °C",
      "Fast cycle times for rapid instrument turnaround",
      "Leaves no toxic residue — loads are safe to handle immediately",
      door === "Double Door" ? "Pass-through double-door barrier separating sterile and non-sterile zones" : "Space-efficient single-door installation",
    ],
    applications: ["Endoscopes and flexible scopes", "Electronic and powered instruments", "Optical and delicate devices", "Rigid and lumened instruments"],
    technology: "A precisely measured dose of hydrogen peroxide is vaporised and diffused throughout the chamber, then energised into a low-temperature plasma that inactivates microorganisms across all load surfaces. Residual peroxide is catalytically broken down into water and oxygen, leaving loads dry and free of harmful residue.",
    techFeatures: (door) => [`${door} configuration`, "Vaporised H\u2082O\u2082 plasma process", "Sub-55 °C operating temperature", "Automated peroxide dosing", "Residue-free, dry cycle output", "Touchscreen PLC control"],
    specs: (m, door, s) => [["Model", m], ["Door type", door], ["Chamber volume", `${s} L`], ["Process", "Low-temperature H\u2082O\u2082 plasma"], ["Operating temperature", "< 55 °C"], ["Sterilant", "Vaporised hydrogen peroxide"]],
    options: ["Automated load-tracking", "Peroxide cassette management", "Network / HIS integration", "Extended data-integrity module"],
  },
  {
    line: "Washer and Disinfectors",
    doors: [
      { door: "Double Door", prefix: "WD", models: ["250", "250.1", "360", "450"] },
    ],
    img: () => PIMG.washerDouble,
    tagline: (m, door, s) => `${door} automated washer-disinfector with ${s} L capacity for validated cleaning and thermal disinfection.`,
    intro: (m, door, s) => `The ${m} is a ${door.toLowerCase()} automated washer-disinfector that cleans, thermally disinfects and dries surgical instruments and utensils in a single validated cycle.`,
    overview: (m, door, s) => `The ${m} performs pre-wash, cleaning, thermal disinfection and drying within one automated cycle, delivering repeatable, A0-rated results ready for sterilization. Programmable process control adapts to varied instrument loads, and each cycle's A0 value is documented for full traceability. The double-door configuration provides a barrier between the soiled and clean sides of a reprocessing department.`,
    benefits: () => [
      "Validated cleaning, thermal disinfection and drying in one cycle",
      "A0-rated thermal disinfection for reliable, documented results",
      "Programmable cycles for a wide range of instrument loads",
      "Pass-through double-door barrier between soiled and clean sides",
    ],
    applications: ["Surgical instrument cleaning", "Anaesthesia and utensil reprocessing", "Container and tray washing", "Laboratory glassware"],
    technology: "Multi-stage rotating spray arms deliver pre-wash, cleaning, thermal rinse and hot-air drying phases at precisely controlled temperatures. The controller continuously monitors and records the A0 value of each cycle, documenting effective thermal disinfection before instruments proceed to sterilization.",
    techFeatures: (door) => [`${door} configuration`, "Multi-stage rotating spray arms", "A0-controlled thermal disinfection", "Integrated hot-air drying phase", "AISI 316L wash chamber", "Touchscreen PLC control"],
    specs: (m, door, s) => [["Model", m], ["Door type", door], ["Chamber capacity", `${s} L`], ["Disinfection", "A0 3000 thermal"], ["Process", "Wash · disinfect · dry"], ["Chamber material", "AISI 316L"]],
    options: ["Instrument and MIS load carriers", "Automatic door and loading system", "Process documentation software", "Water-treatment package"],
  },
  {
    line: "Medical Waste Sterilizers",
    doors: [
      { door: "Pre-Shredding", prefix: "P", models: ["50", "100", "150", "300", "500", "1000", "2000"] },
      { door: "Post-Shredding", prefix: "A", models: ["75", "150", "300", "500", "1000"] },
    ],
    img: () => "/assets/waste/p50-1.webp",
    tagline: (m, door) => `${door} medical waste sterilizer with a capacity of ${WASTE_DATA[m.toLowerCase()].capacity} — for ${WASTE_DATA[m.toLowerCase()].usage}.`,
    intro: (m, door) => {
      const d = WASTE_DATA[m.toLowerCase()];
      return `The ${m} is a ${door.toLowerCase()} medical waste sterilizer with a capacity of ${d.capacity}, engineered for ${d.usage}. It combines automatic elevator loading, in-vessel shredding and saturated-steam sterilization in one fully automated treatment system.`;
    },
    overview: (m) => `The ${m} treats medical waste in a fully automatic cycle. When the cycle begins, the top lid opens and the automatic elevator empties the waste into the device; once the lid closes, PLC-controlled shredders with an anti-blockage feature begin reducing the load — including stainless-steel surgical tools and hard ceramic materials — while a mixer in the upper chamber continuously feeds the shredder. Steam is supplied until a uniform temperature of 134 °C (274 °F) is reached in the upper and lower chambers, and sterilization is completed by holding the load between 134 °C and 138 °C under equivalent pressure for 10 minutes, achieving an 8Log10 reduction in the pathogenic contamination level of the waste. During the cooling phase, excess steam is expelled, cold air reduces the temperature of the sterilized waste, and condensed water is filtered under pressure and vacuum before the treated material is discharged and the next cycle begins automatically.`,
    benefits: () => [
      "Press chamber made of high-quality stainless steel",
      "Fully automated operation with no need for operator intervention",
      "Automatic elevator system for easy loading",
      "Zero water consumption in the sterilization process",
      "Advanced automation control with reporting and visualization software",
      "Online access for monitoring and fault diagnosis",
      "Steam regeneration system for low fuel and electricity consumption",
    ],
    applications: ["Hospitals and clinics", "Industrial medical waste treatment facilities", "On-site infectious waste treatment", "Regional waste processing centres"],
    technology: "Every cycle runs through five fully automatic stages: loading, shredding, heating / sterilization, cooling and filtering, and discharging. PLC-controlled shredders reverse their rotation direction to prevent the blades from jamming, and an integrated steam system with steam regeneration keeps fuel and electricity consumption low. The advanced automation control system provides reporting and visualization software with online access for monitoring and fault diagnosis.",
    techFeatures: (door) => [`${door} configuration`, "Automatic elevator loading system", "PLC-controlled anti-blockage shredders", "Steam sterilization at 134–138 °C with 10 min hold", "8Log10 sterilization efficiency (SAL)", "Cold-air cooling with pressure/vacuum filtration"],
    specs: (m) => [["Model", m]],
    options: [],
  },
];

// Uploaded PN110 / PD110 assets (customer artifacts).
const PNPD_ASSET = "https://customer-assets-v7afamib.emergentagent.net/job_waste-treatment-hub-1/artifacts";
const PNPD_IMG_1 = `${PNPD_ASSET}/jd871qga_pn%20-%20pd%20110%20image.png`;
const PNPD_IMG_2 = `${PNPD_ASSET}/fp7yy4eb_pn-pd%20110%20image%202.png`;
const PNPD_IMG_3 = `${PNPD_ASSET}/f31q2uus_pn-pd%20110%20image%203.png`;
const PNPD_GALLERY = [PNPD_IMG_1, PNPD_IMG_2, PNPD_IMG_3];

// Uploaded 160-series plasma machine photos.
const PNPD160_IMG_1 = `${PNPD_ASSET}/t2iccscc_pn-%20pd%20160%20image.png`;
const PNPD160_IMG_2 = `${PNPD_ASSET}/6u8v1xow_pn-pd%20160%20image%202.png`;
const PNPD160_IMG_3 = `${PNPD_ASSET}/kr7zd1jg_pn-pd%20160%20image%203.png`;
const PNPD160_GALLERY = [PNPD160_IMG_1, PNPD160_IMG_2, PNPD160_IMG_3];

// Uploaded Washer Disinfector (WD) machine photos — shared across all WD models for now.
const WD_IMG_1 = `${PNPD_ASSET}/w8x8kfbw_washer-double%20door.png`;
const WD_IMG_2 = `${PNPD_ASSET}/z74t8qbl_washer%20double%20door%20image%202.png`;
const WD_IMG_3 = `${PNPD_ASSET}/rvw0lo70_washer%20double%20door%20image%203.png`;
const WD_GALLERY = [WD_IMG_1, WD_IMG_2, WD_IMG_3];
// WD technical tables are not approved yet — pages render the prepared section with a pending note.
const WD_PENDING = {
  image: WD_IMG_3,
  gallery: WD_GALLERY,
  imageFit: "contain",
  specsPending: true,
};

// ─── Medical Waste Sterilizers ───────────────────────────────────────────────
// Product-specific data only (no source-brand identity). Local copies of the
// model machine renders live in /public/assets/waste/.
const WASTE_IMG = (slug) => `/assets/waste/${slug}-1.webp`;
const WASTE_GALLERY = (slug) => [1, 2, 3].map((i) => `/assets/waste/${slug}-${i}.webp`);

// Exact per-model values from the approved source reference (no cross-model copying).
const WASTE_DATA = {
  p50:   { capacity: "20 Kg/hour (44 Lbs/H)",      usage: "clinics and small-scale hospitals", dims: "2,700 × 1,500 × 1,500", weight: "2,100", power: "30", sterCap: "15-20",   volume: "75",    cycle: "30",    steamFlow: "100", steam: "6",  elec: "1.2", boiler: true },
  p100:  { capacity: "35 Kg/hour (77 Lbs/H)",      usage: "clinics and small-scale hospitals", dims: "2,700 × 1,600 × 1,600", weight: "2,680", power: "30", sterCap: "25-35",   volume: "150",   cycle: "30",    steamFlow: "140", steam: "7",  elec: "1.5", boiler: true },
  p150:  { capacity: "55 Kg/hour (121 Lbs/H)",     usage: "clinics and small-scale hospitals", dims: "2,700 × 1,700 × 1,700", weight: "2,930", power: "35", sterCap: "45-55",   volume: "225",   cycle: "30",    steamFlow: "170", steam: "8",  elec: "1.7", boiler: true },
  p300:  { capacity: "100 Kg/hour (220 Lbs/H)",    usage: "medium-scale hospitals", dims: "2,000 × 2,000 × 3,600", weight: "2,200", power: "15", sterCap: "80-100",  volume: "400",   cycle: "30",    steamFlow: "270", steam: "11", elec: "4",  boiler: false },
  p500:  { capacity: "175 Kg/hour (385 Lbs/H)",    usage: "medium and large-scale hospitals", dims: "2,250 × 2,250 × 4,200", weight: "2,500", power: "19", sterCap: "150-175", volume: "750",   cycle: "35-45", steamFlow: "320", steam: "15", elec: "5",  boiler: false },
  p1000: { capacity: "250 Kg/hour (550 Lbs/H)",    usage: "large-scale hospitals and industrial sterilization facilities", dims: "2,500 × 2,500 × 5,500", weight: "2,900", power: "20", sterCap: "200-250", volume: "1,400", cycle: "35-45", steamFlow: "470", steam: "20", elec: "9",  boiler: false },
  p2000: { capacity: "450 Kg/hour (990 Lbs/H)",    usage: "large-scale hospitals and industrial sterilization facilities", dims: "3,000 × 3,000 × 7,200", weight: "4,600", power: "35", sterCap: "350-450", volume: "2,800", cycle: "45-60", steamFlow: "700", steam: "40", elec: "15", boiler: false },
  a75:   { capacity: "75 Kg/hour (165 Lbs/H)",     usage: "clinics and small-scale hospitals", dims: "1,500 × 1,500 × 1,500", weight: "1,450", power: "17", sterCap: "75",      volume: "400",   cycle: "30",    steamFlow: "250", steam: "11", elec: "3",  boiler: false },
  a150:  { capacity: "150 Kg/hour (330 Lbs/H)",    usage: "medium-scale hospitals", dims: "2,200 × 1,500 × 1,500", weight: "1,850", power: "17", sterCap: "150",     volume: "870",   cycle: "30",    steamFlow: "290", steam: "13", elec: "4",  boiler: false },
  a300:  { capacity: "300 Kg/hour (661 Lbs/H)",    usage: "medium and large-scale hospitals", dims: "3,000 × 2,000 × 2,000", weight: "2,800", power: "19", sterCap: "300",     volume: "2,000", cycle: "40",    steamFlow: "380", steam: "32", elec: "7",  boiler: false },
  a500:  { capacity: "500 Kg/hour (1102 Lbs/H)",   usage: "large-scale hospitals and industrial sterilization facilities", dims: "3,500 × 2,250 × 2,250", weight: "4,600", power: "35", sterCap: "500", volume: "3,500", cycle: "45",    steamFlow: "670", steam: "45", elec: "11", boiler: false },
  a1000: { capacity: "1000 Kg/hour (2204 Lbs/H)",  usage: "large-scale hospitals and industrial sterilization facilities", dims: "6,000 × 2,500 × 2,500", weight: "2,680", power: "35", sterCap: "1000", volume: "7,500", cycle: "45",    steamFlow: "950", steam: "70", elec: "16", boiler: false },
};

// Same approved horizontal table system as the Plasma pages.
const wasteSpecTable = (slug) => {
  const d = WASTE_DATA[slug];
  return {
    lead: "Model",
    trail: "Compliance",
    leadW: 90,
    trailW: 300,
    groups: [
      { label: "General Information", span: 4 },
      { label: "Operating Features", span: 6 },
      { label: "Utilities / Consumption", span: 4 },
    ],
    widths: [180, 100, 130, 160, 150, 170, 170, 140, 150, 150, 90, 110, 150, 150],
    minWidth: 2390,
    subheaders: [
      "Dimensions (L × W × H) (mm)", "Weight (kg)", "Steam Pressure (Bar max)", "Required Electrical Power (kW)",
      "Sterilization Capacity (kg/h)", "Processing Volume Capacity (L)", "Average Waste Density (kg/m³)", "Average Cycle Time (min)", "Maximum Steam Flow (kg/h)", "Sterilization Efficiency (SAL)",
      "Steam (kg)", "Electricity (kW)", "Water (L)", "Special Consumables",
    ],
    values: [
      d.dims, d.weight, "8", d.power,
      d.sterCap, d.volume, "100-150", d.cycle, d.steamFlow, "8Log10",
      d.steam, d.elec, "None", "None",
    ],
    standards: "PED (2014/68/EU), 2006/42/EC, 2014/30/EU, EN 285",
  };
};

const wasteSpecNotes = (slug) => [
  ...(WASTE_DATA[slug].boiler ? ["P50 – P100 – P150 models include an internal steam boiler."] : []),
  "Electricity consumption for the sterilizer only.",
  "Water consumption of the integrated steam boiler: 7-12 L / cycle.",
];

// Steam-style horizontal specification table for the plasma family (PN/PD), built ONLY
// from the uploaded model-specific specification sheets. Same table logic as
// SteamProduct.jsx: green group headers across the top, subheaders underneath, one
// horizontal data row. "Operating temperature" and "Cycle programs" come from the
// sheet's icon strip.
const plasmaSpecTable = ({ volume, chamberSub, chamberDims, internalDims, weight, shelves, passThrough }) => ({
  // rowSpan-2 headers rendered before and after the grouped columns
  lead: "Model",
  trail: "Standards",
  leadW: 90,
  trailW: 280,
  groups: [
    { label: "General Information", span: 4 },
    { label: "Dimensions", span: 3 },
    { label: "Sterilization Chamber", span: 3 },
    { label: "Operating Information", span: 7 },
    { label: "Safety / Features", span: 5 },
  ],
  // minimum column widths, distributed by content length (never compress)
  widths: [
    100, 120, 80, 230,
    170, 170, 170,
    180, 180, 210,
    120, 120, 130, 150, 170, 150, 170,
    150, 100, 110, 130, 110,
  ],
  minWidth: 3590,
  subheaders: [
    "Nominal Volume", "Effective Usable Volume", "Weight", "Power",
    "External Dimensions (W × H × D)", chamberSub, "Internal Chamber Dimensions (W × H × D)",
    "Sterilization Cell", "Shelves (Trays)", "H₂O₂ Concentrator",
    "Sterilization Temperature", "Operating Temperature", "Cycle Programs", "Air Intake",
    "Plasma Type", "Plasma Location", "Plasma Distribution",
    "Pass-Through Operation", "Excess Load Alarm", "Excess Humidity Alarm", "Excess Cellulose Content Alarm", "Catalytic Converter",
  ],
  values: [
    `${volume} L`, `${volume} L`, weight, "380 ACV • Neutral + ground, max. 4kW",
    "1,910 × 680 × 864 mm", chamberDims, internalDims,
    "Rectangular, 316L stainless steel", shelves, "Increases concentration to > 82% wt for improved efficacy",
    "Average 55 °C", "40–55 °C", "55 / 70 / 85 min", "Through medical-grade HEPA filter",
    "Radio-frequency-excited plasma", "Inside the sterilization cell", "Homogeneous within the sterilization cell",
    passThrough, "Yes", "Yes", "Yes", "Yes",
  ],
  standards: "EN ISO 13485, EN ISO 9001, ISO 14937 (EMC EN 60601-1-2, LVD IEC 61010-2-040, and LVD IEC 61010-1)",
});

const MODEL_OVERRIDES = {
  pn110: {
    image: PNPD_IMG_1,
    gallery: PNPD_GALLERY,
    imageFit: "contain",
    specTable: plasmaSpecTable({
      volume: 110,
      chamberSub: "Chamber Dimensions (W × H × D)",
      chamberDims: "430 × 440 × 739 mm",
      internalDims: "400 × 405 × 710 mm",
      weight: "385 kg",
      shelves: "Two shelves with total load capacity 10 kg",
      passThrough: "Not applicable (single door)",
    }),
  },
  pd110: {
    image: PNPD_IMG_3,
    gallery: PNPD_GALLERY,
    imageFit: "contain",
    specTable: plasmaSpecTable({
      volume: 110,
      chamberSub: "Chamber Dimensions — Double Door (W × H × D)",
      chamberDims: "430 × 440 × 745 mm",
      internalDims: "400 × 405 × 710 mm",
      weight: "415 kg",
      shelves: "Two shelves with total load capacity 10 kg",
      passThrough: "Supported",
    }),
  },
  pn160: {
    image: PNPD160_IMG_2,
    gallery: PNPD160_GALLERY,
    imageFit: "contain",
    specTable: plasmaSpecTable({
      volume: 160,
      chamberSub: "Chamber Dimensions (W × H × D)",
      chamberDims: "492 × 532 × 739 mm",
      internalDims: "460 × 500 × 710 mm",
      weight: "395 kg",
      shelves: "Two shelves with total load capacity 12 kg",
      passThrough: "Not applicable (single door)",
    }),
  },
  pd160: {
    image: PNPD160_IMG_3,
    gallery: PNPD160_GALLERY,
    imageFit: "contain",
    specTable: plasmaSpecTable({
      volume: 160,
      chamberSub: "Chamber Dimensions — Double Door (W × H × D)",
      chamberDims: "492 × 532 × 745 mm",
      internalDims: "460 × 500 × 710 mm",
      weight: "430 kg",
      shelves: "Two shelves with total load capacity 12 kg",
      passThrough: "Supported",
    }),
  },
  wd250: { ...WD_PENDING },
  "wd250-1": { ...WD_PENDING },
  wd360: { ...WD_PENDING },
  wd450: { ...WD_PENDING },
  // PN80: reuse PN110's approved machine photos only (specs stay model-specific/pending).
  pn80: {
    image: PNPD_IMG_1,
    gallery: PNPD_GALLERY,
    imageFit: "contain",
  },
};

// Medical Waste Sterilizers — per-model approved data, local images and process stages.
Object.keys(WASTE_DATA).forEach((slug) => {
  MODEL_OVERRIDES[slug] = {
    image: WASTE_IMG(slug),
    gallery: WASTE_GALLERY(slug),
    imageFit: "contain",
    specTable: wasteSpecTable(slug),
    specNotes: wasteSpecNotes(slug),
    process: ["Loading", "Shredding", "Heating / Sterilization", "Cooling & Filtering", "Discharging"],
  };
});

function buildProducts() {
  const out = [];
  LINES.forEach((L) => {
    L.doors.forEach((d) => {
      d.models.forEach((mNum) => {
        const model = `${d.prefix}${mNum}`;
        const size = parseInt(mNum, 10);
        const slug = model.toLowerCase().replace(/\./g, "-");
        const p = {
          slug,
          name: model,
          model,
          line: L.line,
          category: L.line,
          doorType: d.door,
          image: L.img(size),
          tagline: L.tagline(model, d.door, size),
          intro: L.intro(model, d.door, size),
          overview: L.overview(model, d.door, size),
          benefits: L.benefits(d.door),
          applications: L.applications,
          technology: L.technology,
          techFeatures: L.techFeatures(d.door),
          specs: L.specs(model, d.door, size),
          options: L.options,
        };
        out.push(Object.assign(p, MODEL_OVERRIDES[slug] || {}));
      });
    });
  });
  out.forEach((p) => {
    p.related = out.filter((o) => o.line === p.line && o.slug !== p.slug).slice(0, 3).map((o) => o.slug);
  });
  return out;
}

export const PRODUCTS = buildProducts();
